// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import {
  IRoleSaveAttrs,
  IRoleGetListAttrs,
  IRoleGetDropdownAttrs,
  IRoleGetDetailAttrs,
} from 'interfaces/back-office/master/role/role-view.interface'
import {
  IRoleApiListResponse,
  IRoleApiDetailResponse,
  IRoleApiDropdownResponse,
  IRoleApiNullResponse,
} from 'interfaces/back-office/master/role/role-api.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withHelper_baseResponseDropdownTypesModel, withHelper_paginationProps } from 'models/extensions/with-helper'
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Lodash
import cloneDeep from 'lodash/cloneDeep'
import pick from 'lodash/pick'
import omit from 'lodash/omit'

// Constant
import { LOADING } from 'constant/shared.constant'

// Types
const { model, maybeNull, optional, number, string, array, boolean } = types

// Loading
const { BASE, DETAIL, ACTION, DROPDOWN } = LOADING

const AccessModel = model('Access').props({
  code: optional(number, 0),
  name: optional(string, ''),
  module: optional(string, ''),
})

// Role Props
const roleProps = {
  id: maybeNull(number),
  number: maybeNull(number),
  name: maybeNull(string),
  description: maybeNull(string),
  access: optional(array(AccessModel), []),
  status: maybeNull(boolean),
  createdAt: maybeNull(string),
  updatedAt: maybeNull(string),
}

// Role Model
const RoleList = optional(array(model('RoleList').props(omit(roleProps, ['access']))), [])

// Roles List Model
const RolePaginated = model('RolePaginated').props({ ...withHelper_paginationProps, list: RoleList })

// Role Detail Model
const RoleDetail = model('RoleDetail').props(omit(roleProps, ['number', 'access']))

// Base Model
const RoleStoreModel = types
  .model('Role')
  .props({
    masterRole_loading: withHelper_commonLoadingModel,
    masterRole_list: optional(RolePaginated, {}),
    masterRole_detail: optional(RoleDetail, {}),
    masterRole_dropdown: withHelper_baseResponseDropdownTypesModel('RoleDropdown'),
  })
  .extend(withEnvironment)
  .actions(self => ({
    masterRole_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.masterRole_loading, payload)

      detach(self.masterRole_loading)
      self.masterRole_loading = cast(map)
    },
    masterRole_SET_LIST: (payload: IRoleApiListResponse['res']['data']): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.masterRole_list = cast(mapPayload)
    },
    masterRole_SET_DETAIL: (payload: IRoleApiDetailResponse['res']['data']): void => {
      self.masterRole_detail = cast(cloneDeep(payload))
    },
    masterRole_SET_DROPDOWN: (payload: IRoleApiDropdownResponse['res']['data']): void => {
      self.masterRole_dropdown = cast(payload.filter(dropdown => dropdown.value))
    },
    masterRole_CLEAR_LIST: (): void => {
      self.masterRole_list = cast({})
    },
    masterRole_CLEAR_DETAIL: (): void => {
      self.masterRole_detail = cast({})
    },
    masterRole_CLEAR_DROPDOWN: (): void => {
      self.masterRole_dropdown = cast([])
    },
  }))
  .actions(self => ({
    /**
     * @description Fetch roles
     *
     * @param {IRoleGetListAttrs} payload
     *
     * @return {any} any
     */
    masterRole_fetchList: flow(function* (payload: IRoleGetListAttrs) {
      self.masterRole_SET_LOADING({ type: BASE, value: true })

      const response: IRoleApiListResponse = yield self.environment.masterRoleApi.masterRole_getList(payload)

      if (response.ok) {
        self.masterRole_SET_LIST(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.masterRole_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Fetch role
     *
     * @param {IRoleGetDetailAttrs} payload
     *
     * @return {any} any
     */
    masterRole_fetchDetail: flow(function* (payload: IRoleGetDetailAttrs) {
      self.masterRole_SET_LOADING({ type: DETAIL, value: true })

      const response: IRoleApiDetailResponse = yield self.environment.masterRoleApi.masterRole_getDetail({
        params: { ...payload.params },
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.masterRole_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.masterRole_SET_LOADING({ type: DETAIL, value: false })
    }),

    /**
     * @description Save role
     *
     * @param {IRoleSaveAttrs} payload
     *
     * @return {any} any
     */
    masterRole_save: flow(function* (payload: IRoleSaveAttrs) {
      self.masterRole_SET_LOADING({ type: ACTION, value: true })

      const response: IRoleApiNullResponse = yield self.environment.masterRoleApi.masterRole_save({
        params: { ...payload.params },
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      })

      // Do logic when request is success
      if (response.ok && payload?.onSuccess) payload.onSuccess()

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.masterRole_SET_LOADING({ type: ACTION, value: false })
    }),

    /**
     * @description Fetch role dropdown
     *
     * @param {IRoleGetDropdownAttrs} payload
     *
     * @return {any} any
     */
    masterRole_fetchDropdown: flow(function* (payload: IRoleGetDropdownAttrs) {
      self.masterRole_SET_LOADING({ type: DROPDOWN, value: true })

      const response: IRoleApiDropdownResponse = yield self.environment.masterRoleApi.masterRole_getDropdown({
        requestConfig: { ...payload.requestConfig },
      })
      if (response.ok) {
        self.masterRole_SET_DROPDOWN(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError()
      }

      self.masterRole_SET_LOADING({ type: DROPDOWN, value: false })
    }),
  }))

// Role Ref
export const RoleListRef = model('RoleRef').props(pick(roleProps, ['id', 'name', 'access', 'status']))

export const createRoleStoreDefaultModel = () => optional(RoleStoreModel, {})
